type Formatters = {
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string;
};

export type TransportLrPrintOptions = {
  /** Footer invoice reference (from linked invoice or item invoice nos) */
  asPerInvoiceNo?: string;
  invoiceDate?: string | Date;
  invoiceValue?: number;
  documentTitle?: string;
};

const DEFAULT_CAUTION = 'The Consignment will not be detained re-routed or re-booked without consignee Bank and written permission will be delivered at destination.';

const DEFAULT_NOTICE = `The Consignment covered by this set or Special Lorry Receipt shall be stored at the destination under the control of the Transport Operator and shall be delivered to or to the order of the Consignee Bank whose name is mentioned in the Lorry Receipt. It will under no circumstances be delivered to anyone without the written authority from the Consignee Bank or its order, endorsed on the Consignee Copy or on a separate letter.`;

const esc = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const fmtDate = (date: Date | string | undefined, formatDate: Formatters['formatDate']) => {
  if (!date) return '—';
  try {
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  } catch {
    return formatDate(date, { dateStyle: 'medium' });
  }
};

const moneyParts = (amount: unknown) => {
  const n = Math.round((Number(amount) || 0) * 100) / 100;
  const rs = Math.floor(Math.abs(n));
  const ps = Math.round((Math.abs(n) - rs) * 100);
  return { rs: rs.toFixed(0), ps: String(ps).padStart(2, '0') };
};

function formatItems(bilty: Record<string, any>) {
  if (bilty.items?.length) return bilty.items;
  return [{
    description: 'Freight charges',
    quantity: '',
    actualWeight: bilty.weight,
    chargedWeight: bilty.weight,
  }];
}

function getCompanySettings(company?: Record<string, any> | null) {
  const bs = company?.biltySettings || {};
  return {
    headOffice: bs.headOffice || company?.addressLine1 || '',
    branchOffice: bs.branchOffice || company?.addressLine2 || '',
    transportRoutes: bs.transportRoutes || '',
    cautionText: bs.cautionText || DEFAULT_CAUTION,
    noticeText: bs.noticeText || DEFAULT_NOTICE,
    footerNote: bs.footerNote || '',
    mobileNumbers: bs.mobileNumbers || company?.companyMobile || '',
    email: company?.companyEmail || '',
  };
}

function collectInvoiceNos(bilty: Record<string, any>, override?: string) {
  if (override) return override;
  const fromItems = (bilty.items || [])
    .map((i: Record<string, any>) => i.invoiceNo)
    .filter(Boolean)
    .join(',');
  return fromItems || '—';
}

export function buildTransportLrBodyHtml(
  bilty: Record<string, any>,
  company: Record<string, any> | null | undefined,
  formatters: Formatters,
  options: TransportLrPrintOptions = {},
) {
  const { formatDate } = formatters;
  const settings = getCompanySettings(company);
  const items = formatItems(bilty);
  const companyName = company?.companyName || 'Transport Company';

  const mobiles = String(settings.mobileNumbers || '')
    .split(/[,;\n]+/)
    .map((m) => m.trim())
    .filter(Boolean);

  const charges: Array<[string, unknown, boolean?]> = [
    ['Freight', bilty.freight ?? bilty.freightAmount ?? 0],
    ['Door Coll.', bilty.doorCollection ?? 0],
    ['Door Del.', bilty.doorDelivery ?? 0],
    ['Hamali', bilty.hamali ?? 0],
    ['St.Ch.', bilty.stCharge ?? 0],
    ['Total', bilty.totalAmount ?? bilty.subtotal ?? bilty.freightAmount ?? 0],
    ['Service Tax', Number(bilty.taxAmount) > 0 ? bilty.taxAmount : '-', true],
    ['Advance', bilty.advance ?? 0],
    ['Balance', bilty.balance ?? 0],
  ];

  const chargeStartIndex = items.length > 0 ? 1 : 0;
  const firstCharge = charges[0];

  const itemRows = items.map((item: Record<string, any>, idx: number) => {
    const showFirstCharge = idx === 0 && firstCharge;
    const [label, val, isText] = showFirstCharge ? firstCharge : ['', 0, false];
    const amountCells = showFirstCharge
      ? (isText
        ? `<td colspan="2" style="text-align:center">${esc(val)}</td>`
        : `<td>${esc(label)}</td><td style="text-align:right">${moneyParts(val).rs}.${moneyParts(val).ps}</td>`)
      : `<td></td><td></td>`;
    return `
    <tr>
      <td style="text-align:center">${esc(item.quantity ?? '')}</td>
      <td>${esc(item.description)}</td>
      <td style="text-align:right">${esc(item.actualWeight ?? '')}</td>
      <td style="text-align:right">${esc(item.chargedWeight ?? item.weight ?? '')}</td>
      ${amountCells}
      <td></td>
    </tr>`;
  }).join('');

  const remainingCharges = charges.slice(chargeStartIndex);
  const chargeRows = remainingCharges.map(([label, val, isText], idx) => {
    const isLast = idx === remainingCharges.length - 1;
    const amountCells = isText
      ? `<td colspan="2" style="text-align:center">${esc(val)}</td>`
      : `<td>${esc(label)}</td><td style="text-align:right">${moneyParts(val).rs}.${moneyParts(val).ps}</td>`;
    return `
      <tr>
        <td></td><td></td><td></td><td></td>
        ${amountCells}
        <td style="text-align:center;font-weight:700">${isLast ? esc(bilty.cnType || 'Billed') : ''}</td>
      </tr>`;
  }).join('');

  const insured = String(bilty.insuranceStatus || '').toLowerCase() === 'insured';
  const asPerInvoiceNo = collectInvoiceNos(bilty, options.asPerInvoiceNo);
  const invoiceDate = options.invoiceDate || items.find((i: Record<string, any>) => i.invoiceDate)?.invoiceDate || bilty.date;
  const invoiceValue = options.invoiceValue ?? bilty.totalAmount ?? bilty.freightAmount ?? 0;

  return `
<div class="lr-doc">
  <div class="lr-company">
    <div class="lr-company-name">${esc(companyName)}</div>
    ${settings.headOffice ? `<div><strong>H.Off.:</strong> ${esc(settings.headOffice)}${settings.email ? ` Email : ${esc(settings.email)}` : ''}</div>` : ''}
    ${settings.branchOffice ? `<div><strong>B.Off.:</strong> ${esc(settings.branchOffice)}${settings.email ? ` Email: ${esc(settings.email)}` : ''}</div>` : ''}
    ${mobiles.length ? `<div><strong>Mob.</strong> ${mobiles.map((m) => esc(m)).join(' ')}</div>` : ''}
    ${settings.transportRoutes ? `<div><strong>Transport Service :-</strong> ${esc(settings.transportRoutes)}</div>` : ''}
    ${company?.panNumber ? `<div><strong>PAN No.</strong> ${esc(company.panNumber)}</div>` : ''}
  </div>

  <div class="lr-notices">
    <div class="lr-notice-col">
      <div class="lr-notice-title">CAUTION</div>
      <div class="lr-notice-text">${esc(settings.cautionText)}</div>
      <div class="lr-notice-title" style="margin-top:8px">NOTICE</div>
      <div class="lr-notice-text">${esc(settings.noticeText)}</div>
    </div>
    <div class="lr-notice-side">
      ${company?.gstNumber ? `<div><strong>GST No.</strong> ${esc(company.gstNumber)}</div>` : ''}
      <div class="lr-owner-risk">AT OWNER'S RISK</div>
      <div class="lr-insurance-title">INSURANCE</div>
    </div>
  </div>

  <div class="lr-meta">
    <span><strong>LORRY No:</strong> ${esc(bilty.vehicleNumber || bilty.vehicle?.vehicleNumber || '—')}</span>
    <span><strong>No.:</strong> ${esc(bilty.biltyNumber)}</span>
    <span><strong>Date:</strong> ${esc(fmtDate(bilty.date, formatDate))}</span>
    <span><strong>From:</strong> ${esc(bilty.source)}</span>
    <span><strong>To:</strong> ${esc(bilty.destination)}</span>
  </div>

  <div class="lr-delivery">
    <strong>Delivery Address:</strong> ${esc(bilty.deliveryAddress || bilty.consigneeAddress || '—')}
    ${bilty.deliveryPhone ? `<strong style="margin-left:12px">PH</strong> ${esc(bilty.deliveryPhone)}` : ''}
  </div>

  <div class="lr-insurance-box">
    <div><strong>The Customer has stated that :</strong></div>
    <div class="lr-insurance-check">${insured ? '☐' : '☑'} He has not insured the consignment OR</div>
    <div class="lr-insurance-check">${insured ? '☑' : '☐'} He has insured the consignment</div>
    <table class="lr-ins-table">
      <tr><th>Company</th><th>Policy No.</th><th>Date</th><th>Amount</th><th>Date</th></tr>
      <tr>
        <td>${insured ? esc(bilty.insuranceCompany || '') : ''}</td>
        <td>${esc(bilty.policyNo || '')}</td>
        <td>${bilty.insuranceDate ? esc(fmtDate(bilty.insuranceDate, formatDate)) : ''}</td>
        <td>${Number(bilty.insuranceAmount) > 0 ? esc(moneyParts(bilty.insuranceAmount).rs) : ''}</td>
        <td></td>
      </tr>
    </table>
  </div>

  <div class="lr-party">
    <div><strong>Consignor's Name &amp; Address:</strong> ${esc(bilty.consignorName || bilty.consignor?.name || '—')}</div>
    <div>${esc(bilty.consignorAddress || bilty.consignor?.address || '')}</div>
  </div>
  <div class="lr-party">
    <div><strong>Consignee Name &amp; Address:</strong> ${esc(bilty.consigneeName || bilty.consignee?.name || '—')}</div>
    <div>${esc(bilty.consigneeAddress || bilty.consignee?.address || '')}</div>
  </div>

  <table class="lr-items">
    <thead>
      <tr>
        <th rowspan="2">Quantity</th>
        <th rowspan="2">Description<br/>(said to contain)</th>
        <th colspan="2">Weight in kgs</th>
        <th colspan="2">Amount to Pay/Paid/Due</th>
        <th rowspan="2">CN Type</th>
      </tr>
      <tr>
        <th>Actual</th>
        <th>Charged</th>
        <th>Rs.</th>
        <th>Ps.</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
      ${chargeRows}
    </tbody>
  </table>

  <div class="lr-invoice-ref">
    <span><strong>As Per Invoice No.:</strong> ${esc(asPerInvoiceNo)}</span>
    <span><strong>Invoice Date:</strong> ${esc(fmtDate(invoiceDate, formatDate))}</span>
    <span><strong>Value:</strong> ${esc(moneyParts(invoiceValue).rs)}.${esc(moneyParts(invoiceValue).ps)}</span>
  </div>

  <div class="lr-footer">
    <div class="lr-notice-text">${esc(settings.cautionText)}</div>
    <div class="lr-system-note">This is a system-generated document that does not require signature.</div>
    <div class="lr-for-company"><strong>For ${esc(companyName)}</strong></div>
    ${settings.footerNote ? `<div class="lr-notice-text" style="margin-top:6px">${esc(settings.footerNote)}</div>` : ''}
  </div>
</div>`;
}

const LR_STYLES = `
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 10px; color: #111; font-size: 11px; line-height: 1.3; }
  .lr-doc { max-width: 820px; margin: 0 auto; border: 1px solid #222; padding: 8px; }
  .lr-company { text-align: center; border-bottom: 1px solid #222; padding-bottom: 6px; margin-bottom: 6px; }
  .lr-company-name { font-size: 16px; font-weight: 800; text-transform: uppercase; margin-bottom: 4px; }
  .lr-company div { font-size: 10px; margin: 1px 0; }
  .lr-notices { display: grid; grid-template-columns: 1fr 180px; gap: 8px; border-bottom: 1px solid #222; padding-bottom: 6px; margin-bottom: 6px; }
  .lr-notice-title { font-weight: 800; font-size: 10px; text-decoration: underline; }
  .lr-notice-text { font-size: 9px; text-align: justify; }
  .lr-notice-side { font-size: 10px; border-left: 1px solid #222; padding-left: 8px; }
  .lr-owner-risk { font-weight: 800; margin: 8px 0 4px; text-align: center; font-size: 11px; }
  .lr-insurance-title { font-weight: 800; text-align: center; font-size: 11px; }
  .lr-meta { display: flex; flex-wrap: wrap; gap: 12px; font-size: 10px; border-bottom: 1px solid #222; padding: 4px 0 6px; margin-bottom: 4px; }
  .lr-delivery { font-size: 10px; border-bottom: 1px solid #222; padding: 4px 0 6px; margin-bottom: 4px; }
  .lr-insurance-box { font-size: 10px; border-bottom: 1px solid #222; padding: 4px 0 6px; margin-bottom: 4px; }
  .lr-insurance-check { margin: 2px 0 2px 12px; }
  .lr-ins-table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  .lr-ins-table th, .lr-ins-table td { border: 1px solid #222; padding: 3px 5px; font-size: 9px; }
  .lr-party { font-size: 10px; padding: 4px 0; border-bottom: 1px solid #222; }
  .lr-items { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 10px; }
  .lr-items th, .lr-items td { border: 1px solid #222; padding: 4px 5px; vertical-align: top; }
  .lr-items th { background: #f5f5f5; font-weight: 700; text-align: center; font-size: 9px; }
  .lr-invoice-ref { display: flex; flex-wrap: wrap; gap: 16px; font-size: 10px; margin-top: 6px; padding-top: 4px; border-top: 1px solid #222; }
  .lr-footer { margin-top: 8px; font-size: 9px; text-align: center; }
  .lr-system-note { font-weight: 700; margin: 6px 0 4px; font-size: 10px; }
  .lr-for-company { font-size: 11px; margin-top: 4px; }
  @page { size: A4 portrait; margin: 8mm; }
`;

export function buildTransportLrPrintHtml(
  bilty: Record<string, any>,
  company: Record<string, any> | null | undefined,
  formatters: Formatters,
  options: TransportLrPrintOptions = {},
) {
  const body = buildTransportLrBodyHtml(bilty, company, formatters, options);
  const title = options.documentTitle || `LR ${bilty.biltyNumber || ''}`;
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>${LR_STYLES}</style></head><body>${body}</body></html>`;
}

export function openTransportLrPrintWindow(
  bilty: Record<string, any>,
  company: Record<string, any> | null | undefined,
  formatters: Formatters,
  options: TransportLrPrintOptions = {},
) {
  const html = buildTransportLrPrintHtml(bilty, company, formatters, options);
  const win = window.open('', '_blank', 'width=920,height=900');
  if (!win) {
    window.print();
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 400);
}

export const LR_PRINT_STYLES = LR_STYLES;
